import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { readableTextOn, withAlpha } from '@diana/shared'
import { weekdayHeading } from '../lib/datetime.js'

/**
 * The hourly week: seven day columns, one row per hour, 08:00–20:00 by
 * default. Used by Diana's calendar (she clicks an hour to put a lesson in,
 * or paints many at once) and by the public booking page (a visitor clicks
 * a free slot).
 *
 * Deliberately dumb: it knows nothing about timezones or bookings. Callers
 * hand it ISO days and minutes-since-midnight that they have already
 * resolved in whichever zone they are showing.
 */

export type WeekEventTone = 'booked' | 'pending' | 'done' | 'free' | 'selected' | 'muted'

export type WeekEvent = {
  id: string
  /** 'YYYY-MM-DD' — must be one of `days`, otherwise it is not drawn. */
  day: string
  /** Minutes since midnight. */
  startMin: number
  endMin: number
  label: string
  sub?: string
  tone: WeekEventTone
  /**
   * Overrides the tone's palette with one of Diana's own colours — a client
   * group, a single student, or her time off. The label flips to whichever
   * of ink and white stays readable on it.
   */
  color?: string
  onClick?: () => void
}

export type Cell = { day: string; hour: number }

type Props = {
  days: string[]
  startHour?: number
  endHour?: number
  /** BCP-47 tag for the weekday headings. */
  locale?: string
  today?: string
  /** Extra classes for the hour cell behind everything — "open", "closed", "past". */
  cellClass?: (day: string, hour: number) => string
  onCellClick?: (day: string, hour: number) => void
  events: WeekEvent[]
  /** Something to show under the weekday, e.g. a count of free slots. */
  dayBadge?: (day: string) => ReactNode
  /**
   * Painting mode. Clicking or dragging marks a rectangle of hours instead
   * of opening one — so "Monday to Friday, 16:00–18:00" is one gesture.
   */
  selectMode?: boolean
  isSelected?: (day: string, hour: number) => boolean
  /**
   * Hours that may be painted. A cell this says no to cannot start a
   * gesture, is never highlighted, and drops out of one that sweeps over
   * it — so a drag from Monday into next week still works, and picks up
   * nothing that cannot be acted on.
   */
  canSelect?: (day: string, hour: number) => boolean
  /**
   * ⌘- or Ctrl-click on an hour, in either mode. Picking a handful of
   * scattered hours is a different gesture from sweeping a block, and
   * having to find a mode button first is one step too many.
   */
  onPickClick?: (day: string, hour: number) => void
  /** A gesture that landed entirely on hours `canSelect` refused. */
  onNothingSelectable?: () => void
  /** Called once when the gesture ends, never mid-drag. */
  onSelectRange?: (cells: Cell[], select: boolean) => void
}

/** rem per hour. Tall enough for a name and a time on a 30-minute lesson. */
const HOUR_REM = 3.5

const TONES: Record<WeekEventTone, string> = {
  booked: 'border-gold bg-gold-100 text-ink hover:bg-gold-300/60',
  pending: 'border-dashed border-gold bg-ivory text-gold-800 hover:bg-gold-100',
  done: 'border-sky-300 bg-sky-100 text-ink-soft hover:bg-sky-200',
  free: 'border-gold/60 bg-white text-gold-800 hover:border-gold hover:bg-gold-100',
  selected: 'border-ink bg-ink text-gold-300',
  muted: 'border-sky-200 bg-sky-50 text-ink-faint',
}

type Drag = { fromDay: number; fromHour: number; toDay: number; toHour: number; select: boolean }

export function WeekGrid({
  days,
  startHour = 8,
  endHour = 20,
  locale = 'pl-PL',
  today,
  cellClass,
  onCellClick,
  events,
  dayBadge,
  selectMode = false,
  isSelected,
  canSelect,
  onPickClick,
  onNothingSelectable,
  onSelectRange,
}: Props) {
  const selectable = (day: string, hour: number) => canSelect?.(day, hour) ?? true
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i)
  const gridStart = startHour * 60
  const gridEnd = endHour * 60

  const [drag, setDrag] = useState<Drag | null>(null)

  /**
   * The gesture is committed on pointerup anywhere — including outside the
   * grid, which is where a fast drag usually ends. Until then the rectangle
   * is only a preview, so the parent's selection never flickers mid-drag.
   */
  useEffect(() => {
    if (!drag) return
    const finish = () => {
      const dayFrom = Math.min(drag.fromDay, drag.toDay)
      const dayTo = Math.max(drag.fromDay, drag.toDay)
      const hourFrom = Math.min(drag.fromHour, drag.toHour)
      const hourTo = Math.max(drag.fromHour, drag.toHour)

      const cells: Cell[] = []
      let refused = 0
      for (let d = dayFrom; d <= dayTo; d++) {
        for (let h = hourFrom; h <= hourTo; h++) {
          const day = days[d]
          if (!day) continue
          if (selectable(day, h)) cells.push({ day, hour: h })
          else refused++
        }
      }
      if (cells.length > 0) onSelectRange?.(cells, drag.select)
      // A sweep that lands entirely on refused hours must say so rather than
      // look like a click that missed.
      else if (refused > 0) onNothingSelectable?.()
      setDrag(null)
    }

    window.addEventListener('pointerup', finish)
    window.addEventListener('pointercancel', finish)
    return () => {
      window.removeEventListener('pointerup', finish)
      window.removeEventListener('pointercancel', finish)
    }
  }, [drag, days, onSelectRange])

  const inDragRect = (dayIndex: number, hour: number) =>
    drag !== null &&
    dayIndex >= Math.min(drag.fromDay, drag.toDay) &&
    dayIndex <= Math.max(drag.fromDay, drag.toDay) &&
    hour >= Math.min(drag.fromHour, drag.toHour) &&
    hour <= Math.max(drag.fromHour, drag.toHour)

  return (
    <div className="overflow-x-auto rounded-sm border border-sky-200 bg-white">
      <div className="min-w-[44rem]">
        {/* ── headings ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-[3.25rem_repeat(7,minmax(0,1fr))] border-b border-sky-200">
          <div />
          {days.map((day) => {
            const { weekday, day: num } = weekdayHeading(day, locale)
            const isToday = day === today
            return (
              <div
                key={day}
                className="flex items-end justify-between gap-1 border-l border-sky-100 px-2 py-2"
              >
                <span className="flex items-baseline gap-1.5">
                  <span className="font-roboto text-[0.65rem] font-bold uppercase tracking-widest text-ink-faint">
                    {weekday}
                  </span>
                  <span
                    className={[
                      'font-roboto text-lg font-bold leading-none',
                      isToday ? 'rounded-full bg-gold px-2 py-0.5 text-white' : '',
                    ].join(' ')}
                  >
                    {num}
                  </span>
                </span>
                {dayBadge && <span className="text-right">{dayBadge(day)}</span>}
              </div>
            )
          })}
        </div>

        {/* ── body ─────────────────────────────────────────────────── */}
        <div
          className={[
            'grid grid-cols-[3.25rem_repeat(7,minmax(0,1fr))]',
            // Painting must not scroll the page under the finger, and the
            // drag must not turn every cell into highlighted text.
            selectMode ? 'touch-none select-none' : '',
          ].join(' ')}
        >
          {/* hour labels */}
          <div>
            {hours.map((h) => (
              <div
                key={h}
                style={{ height: `${HOUR_REM}rem` }}
                className="relative border-b border-sky-100 pr-2 text-right"
              >
                <span className="absolute -top-2 right-2 font-roboto text-[0.7rem] font-bold text-ink-faint">
                  {String(h).padStart(2, '0')}:00
                </span>
              </div>
            ))}
            <div className="relative h-0">
              <span className="absolute -top-2 right-2 font-roboto text-[0.7rem] font-bold text-ink-faint">
                {String(endHour).padStart(2, '0')}:00
              </span>
            </div>
          </div>

          {days.map((day, dayIndex) => (
            <div key={day} className="relative border-l border-sky-100">
              {hours.map((h) => {
                const extra = cellClass?.(day, h) ?? ''

                if (selectMode) {
                  const canPick = selectable(day, h)
                  const picked = (isSelected?.(day, h) ?? false) && canPick
                  const previewing = canPick && inDragRect(dayIndex, h)
                  // While dragging over it, a cell shows what it is about to
                  // become, not what it is.
                  const on = previewing ? drag!.select : picked
                  return (
                    <button
                      key={h}
                      type="button"
                      onPointerDown={(e) => {
                        e.preventDefault()
                        // Silently ignoring the press is what this whole change
                        // is against: say why nothing happened.
                        if (!canPick) return onNothingSelectable?.()
                        setDrag({
                          fromDay: dayIndex,
                          fromHour: h,
                          toDay: dayIndex,
                          toHour: h,
                          // Starting on a selected cell erases, starting on
                          // an empty one paints — the spreadsheet gesture.
                          select: !picked,
                        })
                      }}
                      onPointerEnter={() =>
                        setDrag((d) => (d ? { ...d, toDay: dayIndex, toHour: h } : d))
                      }
                      style={{ height: `${HOUR_REM}rem` }}
                      aria-label={`${day} ${String(h).padStart(2, '0')}:00`}
                      aria-pressed={on}
                      aria-disabled={!canPick}
                      // A picked cell drops its usual background entirely rather
                      // than layering on top of it: two background utilities in
                      // one class list are settled by stylesheet order, not by
                      // the order written here, and the selection lost.
                      className={[
                        'relative block w-full border-b border-sky-100 text-left transition',
                        on
                          ? 'bg-ink ring-1 ring-inset ring-gold-300'
                          : canPick
                            ? `${extra} hover:bg-gold-100/60`
                            : `${extra} cursor-not-allowed`,
                      ].join(' ')}
                    />
                  )
                }

                return onCellClick ? (
                  <button
                    key={h}
                    type="button"
                    onClick={(e) => {
                      if ((e.metaKey || e.ctrlKey) && onPickClick) return onPickClick(day, h)
                      onCellClick(day, h)
                    }}
                    style={{ height: `${HOUR_REM}rem` }}
                    aria-label={`${day} ${String(h).padStart(2, '0')}:00`}
                    className={[
                      'block w-full border-b border-sky-100 text-left transition hover:bg-gold-100/60',
                      extra,
                    ].join(' ')}
                  />
                ) : (
                  <div
                    key={h}
                    style={{ height: `${HOUR_REM}rem` }}
                    className={['border-b border-sky-100', extra].join(' ')}
                  />
                )
              })}

              {events
                .filter((e) => e.day === day && e.endMin > gridStart && e.startMin < gridEnd)
                .map((e) => {
                  const top = Math.max(e.startMin, gridStart) - gridStart
                  const bottom = Math.min(e.endMin, gridEnd) - gridStart
                  const style: CSSProperties = {
                    top: `${(top / 60) * HOUR_REM}rem`,
                    height: `calc(${((bottom - top) / 60) * HOUR_REM}rem - 2px)`,
                  }
                  const cls = [
                    'absolute inset-x-0.5 flex flex-col overflow-hidden rounded-sm border px-1.5 py-0.5 text-left font-roboto text-[0.7rem] leading-tight transition',
                    e.color ? '' : TONES[e.tone],
                    // A lesson block must not swallow the drag that crosses it,
                    // but it does stay on top of the selection: she has to be
                    // able to see that the hour she is blocking has a lesson in
                    // it. Blocking never cancels one — it only stops the site
                    // offering the time.
                    selectMode ? 'pointer-events-none z-20 ring-1 ring-white/60' : '',
                  ].join(' ')

                  if (e.color) {
                    Object.assign(style, {
                      backgroundColor: e.color,
                      borderColor: withAlpha(e.color, 0.9),
                      color: readableTextOn(e.color),
                    })
                  }
                  const inner = (
                    <>
                      <span className="truncate font-bold">{e.label}</span>
                      {e.sub && <span className="truncate opacity-80">{e.sub}</span>}
                    </>
                  )
                  return e.onClick && !selectMode ? (
                    <button key={e.id} type="button" onClick={e.onClick} style={style} className={cls}>
                      {inner}
                    </button>
                  ) : (
                    <div key={e.id} style={style} className={cls}>
                      {inner}
                    </div>
                  )
                })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Month / week switch, shared by both calendars so they feel like one thing. */
export function ViewToggle({
  value,
  onChange,
  labels,
}: {
  value: 'month' | 'week'
  onChange: (v: 'month' | 'week') => void
  labels: { month: string; week: string }
}) {
  return (
    <div role="group" className="inline-flex rounded-full border border-sky-200 bg-white p-0.5">
      {(['month', 'week'] as const).map((v) => (
        <button
          key={v}
          type="button"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={[
            'rounded-full px-4 py-1.5 font-roboto text-xs font-bold uppercase tracking-widest transition',
            value === v ? 'bg-ink text-gold-300' : 'text-ink-faint hover:text-ink',
          ].join(' ')}
        >
          {labels[v]}
        </button>
      ))}
    </div>
  )
}
